import React, { useEffect, useState } from 'react';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminCountyLocations() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [err, setErr] = useState('');

  function load() { api.get('/admin/county-locations').then((r) => setItems(r.data.items)); }
  useEffect(load, []);

  async function add() {
    setErr('');
    try {
      await api.post('/admin/county-locations', { name, lat: parseFloat(lat), lon: parseFloat(lon) });
      setName(''); setLat(''); setLon(''); load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed');
    }
  }

  async function remove(id, n) {
    if (!window.confirm(`Remove ${n} from the rotation? The homepage will skip it from tomorrow.`)) return;
    await api.delete(`/admin/county-locations/${id}`);
    load();
  }

  return (
    <div>
      <div className="label text-heritage mb-2">County locations</div>
      <h1 className="font-display text-4xl mb-8">Towns shown in the homepage weather rotation.</h1>

      <HelpCallout
        title="Towns that take turns in the homepage hero"
        description={`Every day, the homepage shows live weather for ONE town from this list (chosen by day-of-year). Visitors can also tap the chip to cycle, and a "Across the county" strip below it shows three other towns from this list as well. The Friday AI newsletter uses the same featured town in its weather summary. Add a town here and from tomorrow it joins the rotation; delete one and it stops appearing immediately.`}
        appearsAt={[
          { label: 'Homepage hero label', where: '"Right now in <Town> · …°C"' },
          { label: 'Homepage hero "Across the county" strip', where: 'shows 3 other towns from this list' },
          { label: '/admin/newsletter Friday digest', where: 'AI prompt uses the featured town for context' },
        ]}
      />

      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-6">
        <div className="label text-heritage mb-3 flex items-center gap-2"><MapPin size={14}/> {items.length} towns in rotation</div>
        <div className="space-y-2 mb-6">
          {items.map((l) => (
            <div key={l.id} className="bg-admin border border-charcoal/10 rounded-xl px-4 py-3 flex items-center justify-between" data-testid={`county-loc-${l.id}`}>
              <div>
                <div className="font-semibold">{l.name}</div>
                <div className="text-xs text-muted">{l.lat.toFixed(4)}, {l.lon.toFixed(4)}</div>
              </div>
              <button onClick={() => remove(l.id, l.name)} data-testid={`delete-${l.id}`} className="p-2 text-heritage hover:bg-heritage/5 rounded-lg" aria-label={`remove ${l.name}`}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 pt-4 border-t border-charcoal/10">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Town name (e.g. Shepshed)" data-testid="loc-name" className="md:col-span-5 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude (e.g. 52.7647)" type="number" step="0.0001" data-testid="loc-lat" className="md:col-span-3 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input value={lon} onChange={(e) => setLon(e.target.value)} placeholder="Longitude (e.g. -1.2962)" type="number" step="0.0001" data-testid="loc-lon" className="md:col-span-3 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <button onClick={add} data-testid="loc-add" className="md:col-span-1 bg-forest text-base rounded-xl px-4 py-2.5 font-semibold inline-flex items-center justify-center"><Plus size={14}/></button>
          {err && <div className="md:col-span-12 text-heritage text-sm">{err}</div>}
        </div>
        <div className="mt-3 text-xs text-muted">
          Tip: get lat/lon from <a href="https://www.latlong.net/" target="_blank" rel="noreferrer" className="underline">latlong.net</a> — search the town name and copy the numbers.
        </div>
      </div>
    </div>
  );
}
